import * as THREE from 'three';
import type { MapBody, MapBodyKind } from '../scenes';
import { formatDistance } from './formatters';

export interface SystemMapOverlayOptions {
  readonly getMapBodies: () => readonly MapBody[];
  readonly getPlayerPosition: () => THREE.Vector3;
  readonly getCurrentDestinationId: () => string | null;
  /** Returns the id of the body the active mission step targets, or
   *  null when no mission marker is active. The map renders this as a
   *  red pulsing ring distinct from the manual selection ring. */
  readonly getMissionDestinationId: () => string | null;
  readonly onSelect: (bodyId: string | null) => void;
  readonly onClose: () => void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const MAP_SIZE = 760;
const MAP_RADIUS = MAP_SIZE / 2;
const CONTENT_RADIUS = MAP_RADIUS - 18;

/**
 * Top-down 2D map of the star system. Star at center, planets/moons at their
 * orbital positions in the XZ plane, station as a marker. Asteroids are not
 * shown. Hover for tooltip; click to set as destination.
 */
export class SystemMapOverlay {
  readonly element: HTMLElement;

  private readonly rootWrap: HTMLElement;
  private readonly svg: SVGSVGElement;
  private readonly bodyLayer: SVGGElement;
  private readonly orbitLayer: SVGGElement;
  private readonly playerLayer: SVGGElement;
  private readonly tooltipEl: HTMLElement;
  private readonly statusEl: HTMLElement;

  private bodies: readonly MapBody[] = [];
  private bodyMarkers = new Map<string, SVGElement>();
  private selectedId: string | null = null;
  private missionId: string | null = null;
  private hoveredId: string | null = null;
  private worldScale = 1;
  private visible = false;
  private rafId: number | null = null;

  constructor(private readonly opts: SystemMapOverlayOptions) {
    this.element = document.createElement('div');
    this.element.className = 'sysmap-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'sysmap-backdrop';

    this.rootWrap = document.createElement('div');
    this.rootWrap.className = 'sysmap-root';

    const header = document.createElement('header');
    header.className = 'sysmap-header';
    const title = document.createElement('h2');
    title.className = 'sysmap-title';
    title.textContent = 'System Map';
    const subtitle = document.createElement('div');
    subtitle.className = 'sysmap-subtitle';
    subtitle.textContent = 'Click any planet, moon or station to mark it as your destination.';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'sysmap-close';
    closeBtn.textContent = 'Close (M)';
    closeBtn.addEventListener('click', () => this.opts.onClose());
    const titleBlock = document.createElement('div');
    titleBlock.append(title, subtitle);
    header.append(titleBlock, closeBtn);

    const board = document.createElement('div');
    board.className = 'sysmap-board';

    this.svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
    this.svg.setAttribute(
      'viewBox',
      `0 0 ${MAP_SIZE} ${MAP_SIZE}`
    );
    this.svg.classList.add('sysmap-svg');

    // Outer circle (system bound).
    const bound = document.createElementNS(SVG_NS, 'circle');
    bound.setAttribute('cx', String(MAP_RADIUS));
    bound.setAttribute('cy', String(MAP_RADIUS));
    bound.setAttribute('r', String(CONTENT_RADIUS + 4));
    bound.setAttribute('fill', 'none');
    bound.setAttribute('stroke', 'rgba(156, 205, 255, 0.18)');
    bound.setAttribute('stroke-width', '1');
    this.svg.appendChild(bound);

    // Cardinal cross hairs.
    const cross1 = document.createElementNS(SVG_NS, 'line');
    cross1.setAttribute('x1', '12');
    cross1.setAttribute('y1', String(MAP_RADIUS));
    cross1.setAttribute('x2', String(MAP_SIZE - 12));
    cross1.setAttribute('y2', String(MAP_RADIUS));
    cross1.setAttribute('stroke', 'rgba(156, 205, 255, 0.07)');
    cross1.setAttribute('stroke-width', '1');
    this.svg.appendChild(cross1);
    const cross2 = document.createElementNS(SVG_NS, 'line');
    cross2.setAttribute('x1', String(MAP_RADIUS));
    cross2.setAttribute('y1', '12');
    cross2.setAttribute('x2', String(MAP_RADIUS));
    cross2.setAttribute('y2', String(MAP_SIZE - 12));
    cross2.setAttribute('stroke', 'rgba(156, 205, 255, 0.07)');
    cross2.setAttribute('stroke-width', '1');
    this.svg.appendChild(cross2);

    this.orbitLayer = document.createElementNS(SVG_NS, 'g') as SVGGElement;
    this.svg.appendChild(this.orbitLayer);

    this.bodyLayer = document.createElementNS(SVG_NS, 'g') as SVGGElement;
    this.svg.appendChild(this.bodyLayer);

    this.playerLayer = document.createElementNS(SVG_NS, 'g') as SVGGElement;
    this.svg.appendChild(this.playerLayer);

    board.appendChild(this.svg);

    this.tooltipEl = document.createElement('div');
    this.tooltipEl.className = 'sysmap-tooltip';

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'sysmap-status';
    this.statusEl.textContent = 'No destination selected';

    const footer = document.createElement('footer');
    footer.className = 'sysmap-footer';
    footer.innerHTML =
      '<div class="sysmap-legend">' +
      '<span class="sysmap-legend-dot sysmap-legend-star"></span>Star' +
      '<span class="sysmap-legend-dot sysmap-legend-planet"></span>Planet' +
      '<span class="sysmap-legend-dot sysmap-legend-moon"></span>Moon' +
      '<span class="sysmap-legend-dot sysmap-legend-station"></span>Station' +
      '<span class="sysmap-legend-dot sysmap-legend-ship"></span>Your ship' +
      '</div>' +
      '<div class="sysmap-keys"><kbd>M</kbd> or <kbd>ESC</kbd> closes · click outside bodies to clear</div>';

    this.rootWrap.append(header, board, this.statusEl, footer);
    this.element.append(backdrop, this.rootWrap, this.tooltipEl);

    // Click on empty area clears selection.
    this.svg.addEventListener('click', (ev) => {
      if (ev.target === this.svg) {
        this.select(null);
      }
    });
    this.svg.addEventListener('mouseleave', () => this.hideTooltip());
  }

  show(): void {
    this.bodies = this.opts.getMapBodies();
    this.selectedId = this.opts.getCurrentDestinationId();
    this.missionId = this.opts.getMissionDestinationId();
    this.computeScale();
    this.renderOrbits();
    this.renderBodies();
    this.refreshStatus();
    this.element.classList.add('is-visible');
    this.visible = true;
    this.startRaf();
  }

  hide(): void {
    this.element.classList.remove('is-visible');
    this.visible = false;
    this.hideTooltip();
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.hide();
    this.element.remove();
  }

  private computeScale(): void {
    let maxRadial = 0;
    const tmp = new THREE.Vector3();
    for (const body of this.bodies) {
      body.getWorldPosition(tmp);
      const r = Math.hypot(tmp.x, tmp.z);
      if (r > maxRadial) maxRadial = r;
    }
    if (maxRadial < 1) maxRadial = 1;
    this.worldScale = (CONTENT_RADIUS - 12) / maxRadial;
  }

  private renderOrbits(): void {
    while (this.orbitLayer.firstChild) {
      this.orbitLayer.removeChild(this.orbitLayer.firstChild);
    }
    const tmp = new THREE.Vector3();
    for (const body of this.bodies) {
      if (body.kind !== 'planet' && body.kind !== 'moon') continue;
      body.getWorldPosition(tmp);
      const r = Math.hypot(tmp.x, tmp.z) * this.worldScale;
      if (r < 4) continue;
      const ring = document.createElementNS(SVG_NS, 'circle');
      ring.setAttribute('cx', String(MAP_RADIUS));
      ring.setAttribute('cy', String(MAP_RADIUS));
      ring.setAttribute('r', String(r));
      ring.setAttribute('fill', 'none');
      ring.setAttribute(
        'stroke',
        body.kind === 'moon'
          ? 'rgba(156, 205, 255, 0.07)'
          : 'rgba(156, 205, 255, 0.16)'
      );
      ring.setAttribute('stroke-width', body.kind === 'moon' ? '0.6' : '1');
      this.orbitLayer.appendChild(ring);
    }
  }

  private renderBodies(): void {
    while (this.bodyLayer.firstChild) {
      this.bodyLayer.removeChild(this.bodyLayer.firstChild);
    }
    this.bodyMarkers.clear();

    const tmp = new THREE.Vector3();
    for (const body of this.bodies) {
      body.getWorldPosition(tmp);
      const sx = MAP_RADIUS + tmp.x * this.worldScale;
      const sz = MAP_RADIUS + tmp.z * this.worldScale;
      const group = document.createElementNS(SVG_NS, 'g');
      group.setAttribute('class', `sysmap-body sysmap-body--${body.kind}`);
      group.setAttribute('data-id', body.id);
      group.style.cursor = 'pointer';

      const visualRadius = this.bodyVisualRadius(body);

      // Mission objective ring — red, rendered first so the manual
      // selection ring (gold) sits on top when both are on the same
      // body. Class toggled in applySelection.
      const missionRing = document.createElementNS(SVG_NS, 'circle');
      missionRing.setAttribute('cx', String(sx));
      missionRing.setAttribute('cy', String(sz));
      missionRing.setAttribute('r', String(visualRadius + 13));
      missionRing.setAttribute('fill', 'none');
      missionRing.setAttribute('stroke', '#ff5d5d');
      missionRing.setAttribute('stroke-width', '2.2');
      missionRing.classList.add('sysmap-mission-ring');
      group.appendChild(missionRing);

      const missionGlow = document.createElementNS(SVG_NS, 'circle');
      missionGlow.setAttribute('cx', String(sx));
      missionGlow.setAttribute('cy', String(sz));
      missionGlow.setAttribute('r', String(visualRadius + 22));
      missionGlow.setAttribute('fill', 'none');
      missionGlow.setAttribute('stroke', '#ff8a8a');
      missionGlow.setAttribute('stroke-width', '1.2');
      missionGlow.setAttribute('opacity', '0.35');
      missionGlow.classList.add('sysmap-mission-glow');
      group.appendChild(missionGlow);

      const missionBadge = document.createElementNS(SVG_NS, 'text');
      missionBadge.setAttribute('x', String(sx));
      missionBadge.setAttribute('y', String(sz + visualRadius + 22));
      missionBadge.setAttribute('text-anchor', 'middle');
      missionBadge.setAttribute('class', 'sysmap-mission-badge');
      missionBadge.setAttribute('fill', '#ff8a8a');
      missionBadge.textContent = '◆ CLICK HERE TO SET DESTINATION ◆';
      group.appendChild(missionBadge);

      // Selection ring (rendered after mission ring so a body that's
      // both manual destination AND mission objective shows the gold
      // selection cleanly on top of the red).
      const ring = document.createElementNS(SVG_NS, 'circle');
      ring.setAttribute('cx', String(sx));
      ring.setAttribute('cy', String(sz));
      ring.setAttribute('r', String(visualRadius + 9));
      ring.setAttribute('fill', 'none');
      ring.setAttribute('stroke', '#ffd389');
      ring.setAttribute('stroke-width', '2.6');
      ring.classList.add('sysmap-selection-ring');
      group.appendChild(ring);

      // Outer glow ring — wider and softer, only visible when selected.
      const glowRing = document.createElementNS(SVG_NS, 'circle');
      glowRing.setAttribute('cx', String(sx));
      glowRing.setAttribute('cy', String(sz));
      glowRing.setAttribute('r', String(visualRadius + 16));
      glowRing.setAttribute('fill', 'none');
      glowRing.setAttribute('stroke', '#ffe38a');
      glowRing.setAttribute('stroke-width', '1.4');
      glowRing.setAttribute('opacity', '0.4');
      glowRing.classList.add('sysmap-selection-glow');
      group.appendChild(glowRing);

      // "DESTINO" badge — only visible when selected.
      const badge = document.createElementNS(SVG_NS, 'text');
      badge.setAttribute('x', String(sx));
      badge.setAttribute('y', String(sz - visualRadius - 14));
      badge.setAttribute('text-anchor', 'middle');
      badge.setAttribute('class', 'sysmap-destination-badge');
      badge.setAttribute('fill', '#ffd389');
      badge.textContent = '◆ DESTINATION ◆';
      group.appendChild(badge);

      let shape: SVGElement;
      if (body.kind === 'station') {
        // Diamond shape.
        const half = visualRadius;
        shape = document.createElementNS(SVG_NS, 'polygon');
        shape.setAttribute(
          'points',
          `${sx},${sz - half} ${sx + half},${sz} ${sx},${sz + half} ${sx - half},${sz}`
        );
      } else {
        shape = document.createElementNS(SVG_NS, 'circle');
        shape.setAttribute('cx', String(sx));
        shape.setAttribute('cy', String(sz));
        shape.setAttribute('r', String(visualRadius));
      }
      const colorHex = `#${body.color.toString(16).padStart(6, '0')}`;
      shape.setAttribute('fill', colorHex);
      shape.setAttribute(
        'stroke',
        body.kind === 'star' ? '#fff7cc' : 'rgba(0,0,0,0.4)'
      );
      shape.setAttribute('stroke-width', '1');
      group.appendChild(shape);

      // Star gets a soft halo.
      if (body.kind === 'star') {
        const halo = document.createElementNS(SVG_NS, 'circle');
        halo.setAttribute('cx', String(sx));
        halo.setAttribute('cy', String(sz));
        halo.setAttribute('r', String(visualRadius + 9));
        halo.setAttribute('fill', 'none');
        halo.setAttribute('stroke', '#ffe38a');
        halo.setAttribute('stroke-width', '1.4');
        halo.setAttribute('opacity', '0.45');
        group.appendChild(halo);
      }

      // Label.
      if (body.kind !== 'moon') {
        const label = document.createElementNS(SVG_NS, 'text');
        label.setAttribute('x', String(sx + visualRadius + 6));
        label.setAttribute('y', String(sz + 4));
        label.setAttribute('class', 'sysmap-label');
        label.setAttribute('fill', '#cad9f0');
        label.textContent = body.name;
        group.appendChild(label);
      }

      group.addEventListener('mouseenter', (ev) =>
        this.showTooltip(body, ev as MouseEvent)
      );
      group.addEventListener('mousemove', (ev) =>
        this.moveTooltip(ev as MouseEvent)
      );
      group.addEventListener('mouseleave', () => this.hideTooltip());
      group.addEventListener('click', (ev) => {
        ev.stopPropagation();
        // Toggle: clicking the current destination clears it.
        if (this.selectedId === body.id) {
          this.select(null);
        } else {
          this.select(body.id);
        }
      });

      this.bodyLayer.appendChild(group);
      this.bodyMarkers.set(body.id, group);
    }

    if (this.selectedId) this.applySelection();
  }

  private bodyVisualRadius(body: MapBody): number {
    switch (body.kind) {
      case 'star':
        return 11;
      case 'planet':
        return body.radius >= 480 ? 10 : body.radius >= 280 ? 8 : 6;
      case 'moon':
        return 4;
      case 'station':
        return 6;
    }
  }

  private select(id: string | null): void {
    this.selectedId = id;
    this.applySelection();
    this.refreshStatus();
    this.opts.onSelect(id);
  }

  private applySelection(): void {
    for (const [id, group] of this.bodyMarkers.entries()) {
      group.classList.toggle('is-selected', id === this.selectedId);
      group.classList.toggle('is-mission-objective', id === this.missionId);
    }
  }

  private refreshStatus(): void {
    if (!this.selectedId) {
      this.statusEl.textContent = 'No destination selected';
      this.statusEl.classList.remove('is-active');
      return;
    }
    const body = this.bodies.find((b) => b.id === this.selectedId);
    if (!body) {
      this.statusEl.textContent = 'No destination selected';
      this.statusEl.classList.remove('is-active');
      return;
    }
    const tmp = new THREE.Vector3();
    body.getWorldPosition(tmp);
    const player = this.opts.getPlayerPosition();
    const distance = tmp.distanceTo(player);
    this.statusEl.innerHTML = `Destination: <strong>${body.name}</strong> · ${this.formatLabel(body.kind)} · ${formatDistance(distance)}`;
    this.statusEl.classList.add('is-active');
  }

  private formatLabel(kind: MapBodyKind): string {
    return kind === 'star'
      ? 'star'
      : kind === 'planet'
        ? 'planet'
        : kind === 'moon'
          ? 'moon'
          : 'station';
  }

  private showTooltip(body: MapBody, ev: MouseEvent): void {
    if (this.hoveredId === body.id) return;
    this.hoveredId = body.id;
    const tmp = new THREE.Vector3();
    body.getWorldPosition(tmp);
    const player = this.opts.getPlayerPosition();
    const distance = tmp.distanceTo(player);
    this.tooltipEl.innerHTML = `
      <div class="sysmap-tooltip-name">${body.name}</div>
      <div class="sysmap-tooltip-kind">${this.formatLabel(body.kind)}</div>
      <div class="sysmap-tooltip-dist">Distance: ${formatDistance(distance)}</div>
    `;
    this.tooltipEl.classList.add('is-visible');
    this.moveTooltip(ev);
  }

  private moveTooltip(ev: MouseEvent): void {
    this.tooltipEl.style.transform = `translate(${ev.clientX + 14}px, ${ev.clientY + 14}px)`;
  }

  private hideTooltip(): void {
    this.hoveredId = null;
    this.tooltipEl.classList.remove('is-visible');
  }

  private startRaf(): void {
    if (this.rafId !== null) return;
    const tick = (): void => {
      if (!this.visible) return;
      this.refreshStatus();
      // Mission target can change while the map is open (e.g. step
      // advances while the player flies). Re-poll every frame and
      // re-apply the class flags so the red ring follows live.
      const nextMissionId = this.opts.getMissionDestinationId();
      if (nextMissionId !== this.missionId) {
        this.missionId = nextMissionId;
        this.applySelection();
      }
      // Reposition orbiting bodies live (planets move slowly).
      const tmp = new THREE.Vector3();
      for (const body of this.bodies) {
        const group = this.bodyMarkers.get(body.id);
        if (!group) continue;
        body.getWorldPosition(tmp);
        const sx = MAP_RADIUS + tmp.x * this.worldScale;
        const sz = MAP_RADIUS + tmp.z * this.worldScale;
        const visualRadius = this.bodyVisualRadius(body);
        const shape = group.querySelector(
          'circle:not(.sysmap-selection-ring):not(.sysmap-mission-ring):not(.sysmap-mission-glow):not(.sysmap-selection-glow), polygon'
        ) as SVGElement | null;
        if (shape) {
          if (shape.tagName === 'circle') {
            shape.setAttribute('cx', String(sx));
            shape.setAttribute('cy', String(sz));
          } else if (shape.tagName === 'polygon') {
            const half = visualRadius;
            shape.setAttribute(
              'points',
              `${sx},${sz - half} ${sx + half},${sz} ${sx},${sz + half} ${sx - half},${sz}`
            );
          }
        }
        const ring = group.querySelector(
          '.sysmap-selection-ring'
        ) as SVGElement | null;
        if (ring) {
          ring.setAttribute('cx', String(sx));
          ring.setAttribute('cy', String(sz));
        }
        const glowRing = group.querySelector(
          '.sysmap-selection-glow'
        ) as SVGElement | null;
        if (glowRing) {
          glowRing.setAttribute('cx', String(sx));
          glowRing.setAttribute('cy', String(sz));
        }
        const missionRing = group.querySelector(
          '.sysmap-mission-ring'
        ) as SVGElement | null;
        if (missionRing) {
          missionRing.setAttribute('cx', String(sx));
          missionRing.setAttribute('cy', String(sz));
        }
        const missionGlow = group.querySelector(
          '.sysmap-mission-glow'
        ) as SVGElement | null;
        if (missionGlow) {
          missionGlow.setAttribute('cx', String(sx));
          missionGlow.setAttribute('cy', String(sz));
        }
        const missionBadge = group.querySelector(
          '.sysmap-mission-badge'
        ) as SVGElement | null;
        if (missionBadge) {
          missionBadge.setAttribute('x', String(sx));
          missionBadge.setAttribute('y', String(sz + visualRadius + 22));
        }
        const badge = group.querySelector(
          '.sysmap-destination-badge'
        ) as SVGElement | null;
        if (badge) {
          badge.setAttribute('x', String(sx));
          badge.setAttribute('y', String(sz - visualRadius - 14));
        }
        const label = group.querySelector(
          'text:not(.sysmap-destination-badge):not(.sysmap-mission-badge)'
        ) as SVGElement | null;
        if (label) {
          label.setAttribute('x', String(sx + visualRadius + 6));
          label.setAttribute('y', String(sz + 4));
        }
      }
      // Player indicator.
      while (this.playerLayer.firstChild) {
        this.playerLayer.removeChild(this.playerLayer.firstChild);
      }
      const playerPos = this.opts.getPlayerPosition();
      const sx = MAP_RADIUS + playerPos.x * this.worldScale;
      const sz = MAP_RADIUS + playerPos.z * this.worldScale;
      const tri = document.createElementNS(SVG_NS, 'polygon');
      tri.setAttribute(
        'points',
        `${sx},${sz - 6} ${sx + 5},${sz + 5} ${sx - 5},${sz + 5}`
      );
      tri.setAttribute('fill', '#ffe38a');
      tri.setAttribute('stroke', '#5a3f08');
      tri.setAttribute('stroke-width', '0.8');
      this.playerLayer.appendChild(tri);

      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }
}

